import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axiosClient from '@/utils/axios';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { generatePath } from 'react-router';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';

import { resolve } from '@/api/endPoints';
import AnalyticsNavbar from '@/components/generic/navbar';
import ConfirmModal from '@/components/confirmModal';
import {
  AD_MANAGER_MEDIA_UPLOAD_MAX_FILES,
  AD_MANAGER_MEDIA_UPLOAD_MAX_VIDEOS,
  FACEBOOK,
  INSTAGRAM,
  META
} from '@/config/constants';
import { PLATFORM_STATIC_ICONS_PNG } from '@/config/icons';
import { LomavisFeatureAvailable1 } from '@/lomavisSharedUi/atoms/links/LomavisFeatureAvailable1';
import {
  getCampaignReviewTableColumns,
  LomavisCampaignBuilder1,
  LomavisCampaignBuilderLocation1,
  LomavisCampaignBuilderOption1,
  LomavisCampaignBuilderTabKey1
} from '@/lomavisSharedUi/templates/sections/LomavisCampaignBuilder1';
import type { LomavisCampaignReviewRowStatusKey1 } from '@/lomavisSharedUi/templates/sections/LomavisCampaignBuilder1/campaignReviewTable';
import { LomavisDataTable1 } from '@/lomavisSharedUi/molecules/tables/LomavisDataTable1';
import { formatCurrencyAmountLabel } from '@/lomavisSharedUi/molecules/inputs/LomavisBudgetControls1';
import { LomavisMediaUploadProgress1 } from '@/lomavisSharedUi/molecules/inputs/LomavisMediaUploadPanel1';
import {
  AdCampaignDraft,
  AdManagerNamedOption,
  clearAdManagerErrors,
  copyAdCampaignDraft,
  deleteAdCampaignDraft,
  fetchAdCampaignDraft,
  fetchAdCampaignDrafts,
  fetchAdManagerCountries,
  fetchAdManagerPixels,
  publishAdCampaignDraft,
  saveAdCampaignDraft,
  setActiveDraftUuid,
  setAdCampaignDraftDisabled,
  uploadAdManagerMediaFile
} from '@/redux/slices/adManager/adManager';
import { fetchMetaAdsCountries } from '@/redux/slices/lomavisCreator/lomavisCreator';
import { getLocationDetails } from '@/redux/slices/location/locationDetails';
import { useDispatch, useSelector } from '@/redux/store';
import { PATH_PAGE } from '@/routes/paths';
import { lomavisShowToastr } from '@/utils/ui/toastr';
import {
  compressImage,
  isCompressibleImage
} from '@/utils/media/compressImage';
import {
  getPlacementCategoriesFromKeys,
  validateMediaConstraints
} from '@/utils/media/validateMediaConstraints';
import { getMediaMetadata } from '@/redux/slices/adManager/mediaMetadata';
import { AD_CAMPAIGNS, AD_MANAGER } from '../dashboard/config';
import {
  AD_MANAGER_MEDIA_ACCEPT,
  MAX_AGE,
  MAX_RADIUS_KM,
  MIN_AGE,
  MIN_RADIUS_KM,
  SPECIAL_AD_CATEGORY_MIN_RADIUS_KM,
  SUPPORTED_MEDIA_FORMATS_LABEL
} from './constants';
import {
  getFieldErrorSummary,
  getPublishValidationSummary,
  getRequestErrorMessage,
  getTranslatedAdManagerErrorMessage
} from './errors';
import {
  clampRadiusKm,
  cleanTranslationMarkerText,
  createDefaultForm,
  mapTargetingLocation,
  sanitizeRawData,
  toDraftPayload,
  toFormState
} from './formState';
import {
  buildMediaValidationNotice,
  formatFileNameList,
  formatFileSize,
  getMediaKind,
  getMediaLibrarySelectionResult,
  getSelectedMediaKind,
  getSupportedMediaContentType,
  getUploadableMediaFiles,
  getUploadPresetMediaUuid,
  getUnsupportedMediaFiles,
  hasMixedMediaUpload,
  MediaValidationNotice,
  parseMediaLibrarySelectionPayload
} from './media';
import { getAdManagerPreviewProfile } from './profiles';
import {
  buildReviewRows,
  getAdCampaignDraftStatusKey,
  getAdCampaignDraftStatusLabel,
  isAdCampaignDraftMutable
} from './reviewRows';
import {
  AdManagerDsaDefaults,
  AdManagerFormCopyDefaults,
  AdManagerFormState,
  AdManagerPublishValidationError,
  SelectedMediaFile
} from './types';
import {
  deriveSpecialAdCategoryCountry,
  getRealSpecialAdCategories,
  hasRestrictedSpecialAdCategory,
  isConversionTrackingObjective,
  isPixelTrackingObjective,
  validateAdManagerPublishForm
} from './validation';
import { AdManagerAudienceEstimate } from './audienceEstimate';
import { AdManagerStatusBanner } from './statusBanner';
import { DeprecatedInterestsNotice } from './deprecatedInterestsNotice';
import {
  DeprecatedInterestSuggestion,
  filterSuggestionsForInterests,
  parseDeprecatedInterestSuggestions
} from './targetingValidation';

export type {
  AdManagerFormState,
  AdManagerPublishValidationError,
  SelectedMediaFile
} from './types';
export { createDefaultForm, toDraftPayload, toFormState } from './formState';
export {
  getUploadableMediaFiles,
  getUnsupportedMediaFiles,
  hasMixedMediaUpload
} from './media';
export { getFieldErrorSummary } from './errors';
export { getAdManagerPreviewProfile } from './profiles';
export {
  buildReviewRows,
  getAdCampaignDraftStatusKey,
  getAdCampaignDraftStatusLabel,
  hasAdCampaignDraftMetaObjects,
  isAdCampaignDraftMutable
} from './reviewRows';
export { validateAdManagerPublishForm } from './validation';

const getMetaAdAccountDsaDefaults = (
  metaAdAccounts: any
): AdManagerDsaDefaults => {
  const metaAdAccount = Array.isArray(metaAdAccounts)
    ? metaAdAccounts[0]
    : null;
  const beneficiary = String(metaAdAccount?.beneficiary || '').trim();
  const payor = String(metaAdAccount?.payor || beneficiary).trim();

  return { beneficiary, payor };
};

const getMetaAdAccountCurrency = (metaAdAccounts: any) => {
  const metaAdAccount = Array.isArray(metaAdAccounts)
    ? metaAdAccounts[0]
    : null;

  return String(metaAdAccount?.currency || '')
    .trim()
    .toUpperCase();
};

const getMetaAdAccountCountry = (metaAdAccounts: any) => {
  const metaAdAccount = Array.isArray(metaAdAccounts)
    ? metaAdAccounts[0]
    : null;

  const country = String(metaAdAccount?.country || '')
    .trim()
    .toUpperCase();
  return /^[A-Z]{2}$/.test(country) ? country : '';
};

const AUDIENCE_ESTIMATE_DEBOUNCE_MS = 600;
const AD_MANAGER_MEDIA_LIBRARY_TRIGGER = 'adManagerMediaUploader';
const MEDIA_LIBRARY_POPUP_EVENT = 'LomavisMediaLibraryPopUpFileSelected';
const serializeFormForDirtyCheck = (form: AdManagerFormState) =>
  JSON.stringify(form);

const isCanceledRequest = (error: any) =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  String(error?.message || '').toLowerCase() === 'canceled';

const clampAge = (age: number) => {
  if (!Number.isFinite(age)) return MIN_AGE;
  return Math.min(MAX_AGE, Math.max(MIN_AGE, age));
};

const hasAudienceEstimateInputs = (form: AdManagerFormState) =>
  (form.locations.length > 0 || form.targetCountries.length > 0) &&
  form.placements.some((placement) => placement.selected);

const getAudienceEstimatePayload = (
  form: AdManagerFormState,
  locationUuid: string
) => {
  const draftPayload = toDraftPayload(form, locationUuid);

  return {
    location_uuid: draftPayload.location_uuid,
    name: draftPayload.name,
    objective: draftPayload.objective,
    placements: draftPayload.placements,
    radius_km: draftPayload.radius_km,
    target_locations: draftPayload.target_locations,
    target_languages: draftPayload.target_languages,
    target_interests: draftPayload.target_interests,
    target_behaviors: draftPayload.target_behaviors,
    target_custom_audiences: draftPayload.target_custom_audiences,
    special_ad_categories: draftPayload.special_ad_categories,
    special_ad_category_country: draftPayload.special_ad_category_country,
    age_min: draftPayload.age_min,
    age_max: draftPayload.age_max,
    gender: draftPayload.gender,
    budget_type: draftPayload.budget_type,
    budget_amount_cents: draftPayload.budget_amount_cents,
    bid_strategy: draftPayload.bid_strategy,
    max_bid_amount_cents: draftPayload.max_bid_amount_cents
  };
};

export const AdManagerPage: React.FC = () => {
  const { t } = useTranslate();
  const { adUuid, locationslug } = useParams();
  const isCreateRoute = Boolean(useMatch(PATH_PAGE.ad_manager_create));
  const isEditorRoute = isCreateRoute || Boolean(adUuid);

  useEffect(() => {
    if (isEditorRoute) {
      window.scrollTo({ top: 0 });
    }
  }, [isEditorRoute, adUuid, isCreateRoute]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actionFooterRef = useRef<HTMLDivElement | null>(null);
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const formDraftUuidRef = useRef<string | null>(null);
  const previousRouteKeyRef = useRef<string>('');
  const audienceEstimateRequestIdRef = useRef(0);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const cancelUploadRequestedRef = useRef(false);
  const searchAbortControllersRef = useRef<
    Record<'cities' | 'interests' | 'languages', AbortController | null>
  >({
    cities: null,
    interests: null,
    languages: null
  });

  const locationSlice = useSelector((state: any) => state.locationDetails);
  const adManager = useSelector((state: any) => state.adManager);
  const locationUuid: string | undefined = locationSlice?.uuid || undefined;
  const locationName: string = locationSlice?.name || '';
  const locationLoading: boolean = Boolean(locationSlice?.isLoading);
  const metaAdAccounts = locationSlice?.platforms_information?.meta_ad_account;
  const dsaDefaults = useMemo(
    () => getMetaAdAccountDsaDefaults(metaAdAccounts),
    [metaAdAccounts]
  );
  const metaAdAccountCurrency = useMemo(
    () => getMetaAdAccountCurrency(metaAdAccounts),
    [metaAdAccounts]
  );
  const metaAdAccountCountry = useMemo(
    () => getMetaAdAccountCountry(metaAdAccounts),
    [metaAdAccounts]
  );
  const formCopyDefaults = useMemo<AdManagerFormCopyDefaults>(
    () => ({
      campaignNameSuffix: t('adManager.defaultCampaignSuffix', 'campaign'),
      defaultAccountName: t('adManager.preview.defaultBusinessName', 'Lomavis'),
      newCampaignName: t('adManager.newCampaign', 'New campaign'),
      sponsoredLabel: t('adManager.preview.sponsored', 'Sponsored')
    }),
    [t]
  );

  const [form, setForm] = useState<AdManagerFormState>(() =>
    createDefaultForm(locationName, dsaDefaults, formCopyDefaults)
  );
  const formBaselineRef = useRef(serializeFormForDirtyCheck(form));
  const [activeTabKey, setActiveTabKey] =
    useState<LomavisCampaignBuilderTabKey1>('campaign');
  const [draftsLoadedForLocation, setDraftsLoadedForLocation] = useState(false);
  const [loadingDraftUuid, setLoadingDraftUuid] = useState<string | null>(null);
  // The preview panel no longer has a placement picker, so this state is
  // seeded from the first available (selected) placement and re-synced when
  // the selection changes — otherwise the profile lookup (Facebook page vs
  // Instagram account) can drift from the placement the child renders.
  const [activePreviewPlacementKey, setActivePreviewPlacementKey] =
    useState('facebook-feed');
  const [uploadProgress, setUploadProgress] =
    useState<LomavisMediaUploadProgress1 | null>(null);
  const [mediaValidationNotice, setMediaValidationNotice] =
    useState<MediaValidationNotice | null>(null);
  const [publishValidationErrors, setPublishValidationErrors] = useState<
    AdManagerPublishValidationError[]
  >([]);
  const [publishAttempted, setPublishAttempted] = useState(false);
  const [locationOptions, setLocationOptions] = useState<
    LomavisCampaignBuilderLocation1[]
  >([]);
  const [targetCountryOptions, setTargetCountryOptions] = useState<
    LomavisCampaignBuilderOption1[]
  >([]);
  const [interestOptions, setInterestOptions] = useState<
    LomavisCampaignBuilderOption1[]
  >([]);
  const [languageOptions, setLanguageOptions] = useState<
    LomavisCampaignBuilderOption1[]
  >([]);
  const [paidAdSub, setPaidAdSub] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [locationLoadError, setLocationLoadError] = useState<string | null>(
    null
  );
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null
  );
  const [audienceEstimate, setAudienceEstimate] = useState<any>(null);
  const [isAudienceEstimateLoading, setIsAudienceEstimateLoading] =
    useState(false);
  const [deprecatedInterestSuggestions, setDeprecatedInterestSuggestions] =
    useState<DeprecatedInterestSuggestion[]>([]);
  const [isTargetingCheckLoading, setIsTargetingCheckLoading] = useState(false);
  const [customAudienceOptions, setCustomAudienceOptions] = useState<
    LomavisCampaignBuilderOption1[]
  >([]);
  const [isLoadingCustomAudiences, setIsLoadingCustomAudiences] =
    useState(false);
  const [customAudiencesError, setCustomAudiencesError] = useState<
    string | null
  >(null);
  const [draftStatusFilter, setDraftStatusFilter] = useState<
    'all' | 'draft' | 'publishing' | 'published' | 'failed'
  >('all');
  const [pendingDeleteDraftUuid, setPendingDeleteDraftUuid] = useState<
    string | null
  >(null);
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [publishBillingAcknowledged, setPublishBillingAcknowledged] =
    useState(false);
  const [pendingToggleDisabled, setPendingToggleDisabled] = useState<{
    uuid: string;
    disable: boolean;
  } | null>(null);

  const activeDraft = useMemo(
    () => {
      const activeUuid = adUuid || adManager.activeDraftUuid;
      return adManager.drafts.find(
        (draft: AdCampaignDraft) => draft.uuid === activeUuid
      );
    },
    [adManager.activeDraftUuid, adManager.drafts, adUuid]
  );
  const activeBudgetCurrency =
    metaAdAccountCurrency || activeDraft?.currency || '';
  const reviewRows = useMemo(
    () =>
      buildReviewRows(
        adManager.drafts,
        activeBudgetCurrency,
        adManager.publishingDraftUuid,
        t
      ),
    [activeBudgetCurrency, adManager.drafts, adManager.publishingDraftUuid, t]
  );
  const activePreviewProfile = useMemo(
    () =>
      getAdManagerPreviewProfile(
        locationSlice?.platforms_information,
        activePreviewPlacementKey,
        locationName || t('adManager.preview.defaultBusinessName', 'Lomavis'),
        {
          [FACEBOOK]: t('adManager.preview.facebookPage', 'Facebook page'),
          [INSTAGRAM]: t(
            'adManager.preview.instagramAccount',
            'Instagram account'
          )
        }
      ),
    [
      activePreviewPlacementKey,
      locationName,
      locationSlice?.platforms_information,
      t
    ]
  );
  const isDraftTableLoading =
    !isEditorRoute &&
    (locationLoading ||
      !locationUuid ||
      isLoadingSubscription ||
      (paidAdSub && !draftsLoadedForLocation));
  const normalizeSpecialAdFormUpdates = (
    updates: Partial<AdManagerFormState>
  ): Partial<AdManagerFormState> => {
    const specialAdCategories =
      updates.specialAdCategories ?? form.specialAdCategories;
    const isRestrictedSpecialAd =
      hasRestrictedSpecialAdCategory(specialAdCategories);
    const rawRadiusKm = clampRadiusKm(updates.radiusKm ?? form.radiusKm);
    const radiusKm = isRestrictedSpecialAd
      ? Math.max(rawRadiusKm, SPECIAL_AD_CATEGORY_MIN_RADIUS_KM)
      : rawRadiusKm;

    let locations = updates.locations ?? form.locations;
    let targetCountries = updates.targetCountries ?? form.targetCountries;

    if (
      updates.targetCountries !== undefined &&
      updates.targetCountries.length > 0
    ) {
      locations = [];
    } else if (
      updates.locations !== undefined &&
      updates.locations.length > 0
    ) {
      targetCountries = [];
    }

    const hasRealSpecialAdCategory =
      getRealSpecialAdCategories(specialAdCategories).length > 0;
    const resolvedCountry = hasRealSpecialAdCategory
      ? deriveSpecialAdCategoryCountry(
          { locations, targetCountries },
          metaAdAccountCountry
        )
      : [];
    const normalizedUpdates: Partial<AdManagerFormState> = {
      ...updates,
      radiusKm,
      locations: locations.map((location) => ({
        ...location,
        radiusKm
      })),
      targetCountries,
      specialAdCategoryCountry: resolvedCountry
    };

    if (isRestrictedSpecialAd) {
      normalizedUpdates.ageRange = [18, 65];
      normalizedUpdates.gender = 'all';
      normalizedUpdates.interests = [];
      normalizedUpdates.behaviors = [];
    }

    return normalizedUpdates;
  };
  const isMediaUploadActive =
    adManager.uploadingMedia || Boolean(uploadProgress);
  const activeDraftStatusKey = getAdCampaignDraftStatusKey(activeDraft);
  const isActiveDraftPublishing = activeDraftStatusKey === 'publishing';
  const isDraftActionActive =
    adManager.savingDraft ||
    Boolean(adManager.publishingDraftUuid) ||
    Boolean(adManager.copyingDraftUuid) ||
    isActiveDraftPublishing;
  const isActiveDraftPublished = activeDraftStatusKey === 'published';
  const shouldHideActiveDraftPublish = isActiveDraftPublished;
  const audienceEstimatePayload = useMemo(() => {
    if (!locationUuid || !hasAudienceEstimateInputs(form)) return null;

    return getAudienceEstimatePayload(form, locationUuid);
  }, [
    form.ageRange,
    form.amount,
    form.behaviors,
    form.bidStrategy,
    form.budgetType,
    form.campaignName,
    form.customAudiences,
    form.gender,
    form.interests,
    form.languages,
    form.locations,
    form.targetCountries,
    form.maxBidAmount,
    form.objective,
    form.placements,
    form.radiusKm,
    form.specialAdCategories,
    form.specialAdCategoryCountry,
    locationUuid
  ]);

  const interestValidationPayload = useMemo(() => {
    if (!locationUuid) return null;
    if (form.interests.length === 0) return null;
    if (!hasAudienceEstimateInputs(form)) return null;

    return getAudienceEstimatePayload(form, locationUuid);
  }, [
    form.interests,
    form.behaviors,
    form.locations,
    form.targetCountries,
    form.placements,
    form.radiusKm,
    form.specialAdCategories,
    form.specialAdCategoryCountry,
    locationUuid
  ]);

  const navigateToAdManagerList = useCallback(
    ({ replace = false }: { replace?: boolean } = {}) => {
      if (!locationslug) return;

      navigate(
        generatePath(PATH_PAGE.ad_manager, {
          locationslug
        }),
        { replace }
      );
    },
    [locationslug, navigate]
  );

  const navigateToAdManagerDraft = useCallback(
    (uuid: string, { replace = false }: { replace?: boolean } = {}) => {
      if (!locationslug || !uuid) return;

      navigate(
        generatePath(PATH_PAGE.ad_manager_draft, {
          adUuid: uuid,
          locationslug
        }),
        { replace }
      );
    },
    [locationslug, navigate]
  );

  const navigateToAdManagerCreate = useCallback(
    ({ replace = false }: { replace?: boolean } = {}) => {
      if (!locationslug) return;

      navigate(
        generatePath(PATH_PAGE.ad_manager_create, {
          locationslug
        }),
        { replace }
      );
    },
    [locationslug, navigate]
  );

  const setFormWithBaseline = useCallback((nextForm: AdManagerFormState) => {
    formBaselineRef.current = serializeFormForDirtyCheck(nextForm);
    setForm(nextForm);
  }, []);

  const setFormFromDraft = useCallback(
    (draft: AdCampaignDraft) => {
      setFormWithBaseline(
        toFormState(
          draft,
          locationName,
          adManager.pixelOptions,
          adManager.pixelsLoaded,
          dsaDefaults,
          formCopyDefaults
        )
      );
    },
    [
      adManager.pixelOptions,
      adManager.pixelsLoaded,
      dsaDefaults,
      formCopyDefaults,
      locationName,
      setFormWithBaseline
    ]
  );

  const resetFormToDefault = useCallback(() => {
    const nextForm = createDefaultForm(
      locationName,
      dsaDefaults,
      formCopyDefaults
    );
    setFormWithBaseline(nextForm);
    setPublishValidationErrors([]);
    setActivePreviewPlacementKey('facebook-feed');
    setActiveTabKey('campaign');
    return nextForm;
  }, [dsaDefaults, formCopyDefaults, locationName, setFormWithBaseline]);

  const isFormDirty = useMemo(
    () =>
      isEditorRoute &&
      serializeFormForDirtyCheck(form) !== formBaselineRef.current,
    [form, isEditorRoute]
  );
  const hasCampaignName = cleanTranslationMarkerText(form.campaignName).length > 0;

  const confirmDiscardChanges = useCallback(() => {
    if (!isFormDirty) return true;

    return window.confirm(
      t(
        'adManager.discardChangesConfirm',
        'Discard unsaved campaign changes?'
      )
    );
  }, [isFormDirty, t]);

  const leaveEditorForList = useCallback(
    ({ replace = false }: { replace?: boolean } = {}) => {
      if (isMediaUploadActive || isDraftActionActive) return;
      if (!confirmDiscardChanges()) return;

      dispatch(setActiveDraftUuid(null));
      resetFormToDefault();
      navigateToAdManagerList({ replace });
    },
    [
      confirmDiscardChanges,
      dispatch,
      isDraftActionActive,
      isMediaUploadActive,
      navigateToAdManagerList,
      resetFormToDefault
    ]
  );

  const adManagerNavbarActions = useMemo(
    () => [
      {
        label: AD_CAMPAIGNS,
        onClick: () => {
          if (!locationslug) return;

          navigate(
            generatePath(PATH_PAGE.ad_analytics, {
              locationslug
            })
          );
        },
        translationFallback: 'Campaigns',
        updateHash: false
      },
      {
        label: AD_MANAGER,
        onClick: () => leaveEditorForList(),
        active: true,
        default: true,
        skipInitialOnClick: true,
        translationFallback: 'Ad Manager',
        updateHash: false
      }
    ],
    [leaveEditorForList, locationslug, navigate]
  );

  const loadLocationDetails = useCallback(async () => {
    if (!locationslug) return;

    setLocationLoadError(null);
    try {
      await dispatch(getLocationDetails(locationslug));
    } catch (error: any) {
      setLocationLoadError(
        getRequestErrorMessage(
          error,
          t('adManager.locationLoadFailed', 'Failed to load location.')
        )
      );
    }
  }, [dispatch, locationslug, t]);

  const loadSubscriptionData = useCallback(async () => {
    setIsLoadingSubscription(true);
    setSubscriptionError(null);
    try {
      const url = resolve('current_subscription', {}, {});
      const { data } = await axiosClient.get(url);
      setPaidAdSub(
        Boolean(data?.social_media_management_subscription?.paid_ad_reporting)
      );
    } catch (error: any) {
      setSubscriptionError(
        getRequestErrorMessage(
          error,
          t(
            'adManager.subscriptionLoadFailed',
            'Failed to load subscription status.'
          )
        )
      );
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [t]);

  const loadDraftsPage = useCallback(
    async (pageUrl?: string | null) => {
      if (!locationUuid) return;

      setDraftsLoadedForLocation(false);
      try {
        await dispatch(
          fetchAdCampaignDrafts({
            locationUuid,
            pageUrl,
            status:
              draftStatusFilter === 'all' ? undefined : draftStatusFilter,
            onError: () =>
              lomavisShowToastr(
                t(
                  'adManager.fetchCampaignsFailed',
                  'Failed to load campaigns.'
                ),
                'error'
              )
          })
        );
      } finally {
        setDraftsLoadedForLocation(true);
      }
    },
    [dispatch, draftStatusFilter, locationUuid, t]
  );

  useEffect(() => {
    void loadLocationDetails();
  }, [loadLocationDetails]);

  useEffect(() => {
    void loadSubscriptionData();
  }, [loadSubscriptionData]);

  // Match the builder's preview-placement fallback: first selected, else
  // first available. Without this the profile shown next to the ad (page
  // name/avatar) can be Facebook while the live preview renders the
  // Instagram placement label, e.g. on an Instagram-only campaign.
  useEffect(() => {
    const selectedIds = form.placements
      .filter((placement) => placement.selected)
      .map((placement) => placement.id);
    const availableIds = selectedIds.length
      ? selectedIds
      : form.placements.map((placement) => placement.id);
    if (!availableIds.length) return;
    if (!availableIds.includes(activePreviewPlacementKey)) {
      setActivePreviewPlacementKey(availableIds[0]);
    }
  }, [form.placements, activePreviewPlacementKey]);

  const loadCustomAudiences = useCallback(async () => {
    if (!locationUuid) return;
    setIsLoadingCustomAudiences(true);
    setCustomAudiencesError(null);
    try {
      const url = resolve(
        'ad_campaign_custom_audiences',
        {},
        { location_uuid: locationUuid }
      );
      const { data } = await axiosClient.get(url);
      const rows = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setCustomAudienceOptions(
        rows
          .map((row: any) => ({
            id: String(row.id ?? row.uuid ?? ''),
            name: String(row.name ?? row.title ?? ''),
            raw_data: sanitizeRawData(row.raw_data ?? row)
          }))
          .filter(
            (row: LomavisCampaignBuilderOption1) => row.id && row.name
          )
      );
    } catch (error: any) {
      setCustomAudienceOptions([]);
      setCustomAudiencesError(
        getRequestErrorMessage(
          error,
          t(
            'adManager.fetchCustomAudiencesFailed',
            'Failed to load custom audiences from Meta.'
          ),
          t
        )
      );
    } finally {
      setIsLoadingCustomAudiences(false);
    }
  }, [locationUuid, t]);

  useEffect(() => {
    if (!locationUuid) return;
    if (isLoadingSubscription || !paidAdSub) return;
    void dispatch(fetchAdManagerCountries());
    void dispatch(
      fetchAdManagerPixels({
        locationUuid,
        onError: () =>
          lomavisShowToastr(
            t('adManager.fetchPixelsFailed', 'Failed to fetch Meta pixels.'),
            'error'
          )
      })
    );
    void loadCustomAudiences();
  }, [
    dispatch,
    isLoadingSubscription,
    loadCustomAudiences,
    locationUuid,
    paidAdSub,
    t
  ]);

  useEffect(() => {
    if (!locationUuid) return;
    setDraftsLoadedForLocation(false);
    if (isLoadingSubscription || !paidAdSub) return;
    void loadDraftsPage();
  }, [
    isLoadingSubscription,
    loadDraftsPage,
    locationUuid,
    paidAdSub
  ]);

  useEffect(() => {
    const routeKey = isCreateRoute
      ? `create:${locationslug || ''}`
      : adUuid
      ? `edit:${adUuid}`
      : `list:${locationslug || ''}`;
    const routeChanged = previousRouteKeyRef.current !== routeKey;
    previousRouteKeyRef.current = routeKey;

    if (isCreateRoute) {
      if (routeChanged || form.uuid || adManager.activeDraftUuid) {
        dispatch(setActiveDraftUuid(null));
        resetFormToDefault();
      }
      return;
    }

    if (!adUuid) {
      if (routeChanged || form.uuid || adManager.activeDraftUuid) {
        dispatch(setActiveDraftUuid(null));
        resetFormToDefault();
      }
      return;
    }

    if (isLoadingSubscription || !paidAdSub) return;

    const draft = adManager.drafts.find(
      (row: AdCampaignDraft) =>
        row.uuid === adUuid &&
        (!locationUuid ||
          !row.location_uuid ||
          row.location_uuid === locationUuid)
    );

    if (draft) {
      if (
        routeChanged ||
        form.uuid !== adUuid ||
        adManager.activeDraftUuid !== adUuid
      ) {
        dispatch(setActiveDraftUuid(adUuid));
        setFormWithBaseline(
          toFormState(
            draft,
            locationName,
            adManager.pixelOptions,
            adManager.pixelsLoaded,
            dsaDefaults,
            formCopyDefaults
          )
        );
        setPublishValidationErrors([]);
        setActiveTabKey('campaign');
        setActivePreviewPlacementKey('facebook-feed');
      }
      return;
    }

    if (
      locationUuid &&
      draftsLoadedForLocation &&
      !adManager.loadingDrafts &&
      loadingDraftUuid !== adUuid
    ) {
      setLoadingDraftUuid(adUuid);
      void dispatch(
        fetchAdCampaignDraft({
          uuid: adUuid,
          onSuccess: (fetchedDraft) => {
            if (
              fetchedDraft.location_uuid &&
              fetchedDraft.location_uuid !== locationUuid
            ) {
              lomavisShowToastr(
                t('adManager.draftNotFound', 'Campaign draft not found.'),
                'error'
              );
              navigateToAdManagerList({ replace: true });
              return;
            }

            dispatch(setActiveDraftUuid(adUuid));
            setFormWithBaseline(
              toFormState(
                fetchedDraft,
                locationName,
                adManager.pixelOptions,
                adManager.pixelsLoaded,
                dsaDefaults,
                formCopyDefaults
              )
            );
            setPublishValidationErrors([]);
            setActiveTabKey('campaign');
            setActivePreviewPlacementKey('facebook-feed');
          },
          onError: () => {
            lomavisShowToastr(
              t('adManager.draftNotFound', 'Campaign draft not found.'),
              'error'
            );
            navigateToAdManagerList({ replace: true });
          }
        })
      )
        .catch(() => undefined)
        .finally(() => setLoadingDraftUuid(null));
    }
  }, [
    adUuid,
    adManager.activeDraftUuid,
    adManager.drafts,
    adManager.loadingDrafts,
    adManager.pixelOptions,
    adManager.pixelsLoaded,
    dispatch,
    draftsLoadedForLocation,
    dsaDefaults,
    form.uuid,
    formCopyDefaults,
    isCreateRoute,
    isLoadingSubscription,
    loadingDraftUuid,
    locationName,
    locationslug,
    locationUuid,
    navigateToAdManagerList,
    paidAdSub,
    resetFormToDefault,
    setFormWithBaseline,
    t
  ]);

  useEffect(() => {
    if (!isCreateRoute || isFormDirty) return;

    const nextForm = createDefaultForm(
      locationName,
      dsaDefaults,
      formCopyDefaults
    );
    if (serializeFormForDirtyCheck(nextForm) !== formBaselineRef.current) {
      setFormWithBaseline(nextForm);
    }
  }, [
    dsaDefaults,
    formCopyDefaults,
    isCreateRoute,
    isFormDirty,
    locationName,
    setFormWithBaseline
  ]);

  useEffect(() => {
    setPublishValidationErrors([]);
    setPublishAttempted(false);
  }, [activeDraft?.uuid]);

  useEffect(() => {
    if (!adManager.pixelsLoaded) return;

    setForm((currentForm) => {
      if (!currentForm.pixel) return currentForm;
      const wasPristine =
        serializeFormForDirtyCheck(currentForm) === formBaselineRef.current;

      const matchingPixel = adManager.pixelOptions.find(
        (option: AdManagerNamedOption) =>
          String(option.id) === String(currentForm.pixel?.id)
      );
      const nextPixel = matchingPixel
        ? { id: String(matchingPixel.id), name: matchingPixel.name }
        : null;

      if (
        currentForm.pixel?.id === nextPixel?.id &&
        currentForm.pixel?.name === nextPixel?.name
      ) {
        return currentForm;
      }

      const nextForm = { ...currentForm, pixel: nextPixel };
      if (wasPristine) {
        formBaselineRef.current = serializeFormForDirtyCheck(nextForm);
      }
      return nextForm;
    });
  }, [activeDraft?.uuid, adManager.pixelOptions, adManager.pixelsLoaded]);

  useEffect(() => {
    formDraftUuidRef.current = form.uuid || null;
  }, [form.uuid]);

  useEffect(() => {
    audienceEstimateRequestIdRef.current += 1;
    const requestId = audienceEstimateRequestIdRef.current;

    if (!isEditorRoute || !audienceEstimatePayload) {
      setAudienceEstimate(null);
      setIsAudienceEstimateLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsAudienceEstimateLoading(true);

    const timeoutId = window.setTimeout(() => {
      const estimateUrl = resolve('ad_campaign_audience_estimate', {}, {});

      void axiosClient
        .post(estimateUrl, audienceEstimatePayload)
        .then(({ data }) => {
          if (cancelled || audienceEstimateRequestIdRef.current !== requestId) {
            return;
          }

          setAudienceEstimate(data);
        })
        .catch(() => {
          if (cancelled || audienceEstimateRequestIdRef.current !== requestId) {
            return;
          }

          setAudienceEstimate(null);
        })
        .finally(() => {
          if (cancelled || audienceEstimateRequestIdRef.current !== requestId) {
            return;
          }

          setIsAudienceEstimateLoading(false);
        });
    }, AUDIENCE_ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [audienceEstimatePayload, isEditorRoute]);

  useEffect(() => {
    if (!isEditorRoute) return undefined;

    if (form.interests.length === 0) {
      setDeprecatedInterestSuggestions([]);
      return undefined;
    }

    if (!interestValidationPayload) return undefined;

    let cancelled = false;
    const validateUrl = resolve('ad_campaign_validate_interests', {}, {});

    const timeoutId = window.setTimeout(() => {
      void axiosClient
        .post(validateUrl, interestValidationPayload)
        .then(({ data }) => {
          if (cancelled) return;
          const suggestions = Array.isArray(data?.deprecated_interests)
            ? (data.deprecated_interests as DeprecatedInterestSuggestion[])
            : [];
          setDeprecatedInterestSuggestions(suggestions);
        })
        .catch(() => {
          // Leave the last known suggestions in place; the pre-publish check
          // will re-run against the live endpoint before submit.
        });
    }, AUDIENCE_ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [form.interests.length, interestValidationPayload, isEditorRoute]);

  const showMediaUploadLimitReached = useCallback(() => {
    lomavisShowToastr(
      t(
        'adManager.mediaUploadLimitReached',
        'You can upload up to {maxFiles} media files. Remove a file before adding another.',
        { maxFiles: AD_MANAGER_MEDIA_UPLOAD_MAX_FILES }
      ),
      'error'
    );
  }, [t]);

  const handleMediaLibrarySelection = useCallback(
    (assets: ReturnType<typeof parseMediaLibrarySelectionPayload>) => {
      if (isMediaUploadActive) return;

      if (form.selectedMediaFiles.length >= AD_MANAGER_MEDIA_UPLOAD_MAX_FILES) {
        showMediaUploadLimitReached();
        return;
      }

      const selectionResult = getMediaLibrarySelectionResult(
        assets,
        form.selectedMediaFiles
      );

      if (selectionResult.rejectedUnsupportedCount > 0) {
        lomavisShowToastr(
          `${t(
            'adManager.unsupportedMediaFormat',
            'Unsupported file format'
          )}. ${t(
            'adManager.supportedMediaFormatHelp',
            'Upload {formats} files only.',
            { formats: SUPPORTED_MEDIA_FORMATS_LABEL }
          )}`,
          'error'
        );
        return;
      }

      if (selectionResult.hasMixedMedia) {
        lomavisShowToastr(
          t(
            'adManager.mixedMediaUploadNotAllowed',
            'Use either images (JPEG/PNG) or videos (MP4/MOV) in one campaign, not both.'
          ),
          'error'
        );
        return;
      }

      if (selectionResult.files.length === 0) return;

      setForm((currentForm) => ({
        ...currentForm,
        selectedMediaFiles: [
          ...currentForm.selectedMediaFiles,
          ...selectionResult.files
        ]
      }));
      setPublishValidationErrors([]);
      dispatch(clearAdManagerErrors());

      if (selectionResult.reachedLimit) {
        showMediaUploadLimitReached();
      }
    },
    [
      dispatch,
      form.selectedMediaFiles,
      isMediaUploadActive,
      showMediaUploadLimitReached,
      t
    ]
  );

  useEffect(() => {
    const handleMediaLibraryPopupEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{
        data?: unknown;
        trigger?: string;
      }>;

      if (customEvent.detail?.trigger !== AD_MANAGER_MEDIA_LIBRARY_TRIGGER) {
        return;
      }

      handleMediaLibrarySelection(
        parseMediaLibrarySelectionPayload(customEvent.detail?.data)
      );
    };

    window.addEventListener(
      MEDIA_LIBRARY_POPUP_EVENT,
      handleMediaLibraryPopupEvent
    );

    return () => {
      window.removeEventListener(
        MEDIA_LIBRARY_POPUP_EVENT,
        handleMediaLibraryPopupEvent
      );
    };
  }, [handleMediaLibrarySelection]);

  useEffect(
    () => () => {
      uploadAbortControllerRef.current?.abort();
      Object.values(searchAbortControllersRef.current).forEach((controller) =>
        controller?.abort()
      );
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    },
    []
  );

  const navBarHeight =
    (document.getElementById('kt_app_header')?.clientHeight || 0) + 15 * 2 + 50;

  const updateForm = (patch: Partial<AdManagerFormState>) => {
    setForm((currentForm) => ({ ...currentForm, ...patch }));
    setPublishValidationErrors([]);
    setPublishAttempted(false);
    dispatch(clearAdManagerErrors());
  };

  const persistedDeprecatedInterestSuggestions = useMemo(() => {
    const parsed = parseDeprecatedInterestSuggestions(
      activeDraft?.failure_message
    );
    return parsed?.suggestions ?? [];
  }, [activeDraft?.failure_message]);

  const mergedDeprecatedInterestSuggestions = useMemo(() => {
    const byId = new Map<string, DeprecatedInterestSuggestion>();
    [
      ...persistedDeprecatedInterestSuggestions,
      ...deprecatedInterestSuggestions
    ].forEach((suggestion) => {
      if (!byId.has(suggestion.deprecated.id)) {
        byId.set(suggestion.deprecated.id, suggestion);
      }
    });
    return Array.from(byId.values());
  }, [persistedDeprecatedInterestSuggestions, deprecatedInterestSuggestions]);

  const activeDeprecatedInterestSuggestions = useMemo(
    () =>
      filterSuggestionsForInterests(
        mergedDeprecatedInterestSuggestions,
        form.interests.map((interest) => String(interest.id))
      ),
    [mergedDeprecatedInterestSuggestions, form.interests]
  );

  const replaceDeprecatedInterest = useCallback(
    (suggestion: DeprecatedInterestSuggestion) => {
      if (!suggestion.alternative) return;
      setForm((currentForm) => {
        const alreadySelected = currentForm.interests.some(
          (interest) =>
            String(interest.id) === String(suggestion.alternative!.id)
        );
        const withoutDeprecated = currentForm.interests.filter(
          (interest) => String(interest.id) !== suggestion.deprecated.id
        );
        return {
          ...currentForm,
          interests: alreadySelected
            ? withoutDeprecated
            : [
                ...withoutDeprecated,
                {
                  id: suggestion.alternative!.id,
                  name: suggestion.alternative!.name
                }
              ]
        };
      });
      setPublishValidationErrors([]);
      setPublishAttempted(false);
      dispatch(clearAdManagerErrors());
    },
    [dispatch]
  );

  const removeDeprecatedInterest = useCallback(
    (suggestion: DeprecatedInterestSuggestion) => {
      setForm((currentForm) => ({
        ...currentForm,
        interests: currentForm.interests.filter(
          (interest) => String(interest.id) !== suggestion.deprecated.id
        )
      }));
      setPublishValidationErrors([]);
      setPublishAttempted(false);
      dispatch(clearAdManagerErrors());
    },
    [dispatch]
  );

  const replaceAllDeprecatedInterests = useCallback(() => {
    setForm((currentForm) => {
      const selectedIds = new Set(
        currentForm.interests.map((interest) => String(interest.id))
      );
      const next = [...currentForm.interests];
      activeDeprecatedInterestSuggestions.forEach((suggestion) => {
        if (!suggestion.alternative) return;
        const index = next.findIndex(
          (interest) => String(interest.id) === suggestion.deprecated.id
        );
        if (index === -1) return;
        const alternativeId = String(suggestion.alternative.id);
        if (selectedIds.has(alternativeId)) {
          next.splice(index, 1);
        } else {
          next[index] = {
            id: suggestion.alternative.id,
            name: suggestion.alternative.name
          };
          selectedIds.add(alternativeId);
        }
      });
      return { ...currentForm, interests: next };
    });
    setPublishValidationErrors([]);
    setPublishAttempted(false);
    dispatch(clearAdManagerErrors());
  }, [activeDeprecatedInterestSuggestions, dispatch]);

  const runPrePublishTargetingCheck = useCallback(async () => {
    if (!locationUuid) return true;
    if (form.interests.length === 0) return true;
    if (!interestValidationPayload) return true;

    const validateUrl = resolve('ad_campaign_validate_interests', {}, {});

    setIsTargetingCheckLoading(true);
    try {
      const { data } = await axiosClient.post(
        validateUrl,
        interestValidationPayload
      );
      const suggestions = Array.isArray(data?.deprecated_interests)
        ? (data.deprecated_interests as DeprecatedInterestSuggestion[])
        : [];
      setDeprecatedInterestSuggestions(suggestions);
      const active = filterSuggestionsForInterests(
        suggestions,
        form.interests.map((interest) => String(interest.id))
      );
      return active.length === 0;
    } catch (error: any) {
      const detail =
        typeof error?.response?.data?.detail === 'string'
          ? error.response.data.detail
          : null;
      if (detail) {
        lomavisShowToastr(detail, 'error');
      }
      return activeDeprecatedInterestSuggestions.length === 0;
    } finally {
      setIsTargetingCheckLoading(false);
    }
  }, [
    activeDeprecatedInterestSuggestions,
    form.interests,
    interestValidationPayload,
    locationUuid
  ]);

  const openNewDraftEditor = () => {
    if (isMediaUploadActive || isDraftActionActive) {
      return;
    }
    dispatch(setActiveDraftUuid(null));
    resetFormToDefault();
    navigateToAdManagerCreate();
  };

  const openDraftEditor = (uuid: string) => {
    if (isMediaUploadActive || isDraftActionActive || !uuid) {
      return;
    }
    const draft = adManager.drafts.find(
      (row: AdCampaignDraft) => row.uuid === uuid
    );
    dispatch(setActiveDraftUuid(uuid));
    setFormWithBaseline(
      toFormState(
        draft,
        locationName,
        adManager.pixelOptions,
        adManager.pixelsLoaded,
        dsaDefaults,
        formCopyDefaults
      )
    );
    setPublishValidationErrors([]);
    setActiveTabKey('campaign');
    navigateToAdManagerDraft(uuid);
  };

  const closeEditor = () => {
    leaveEditorForList();
  };

  const handleSearch = async (
    type: 'cities' | 'interests' | 'languages',
    query: string,
    setLoading: (loading: boolean) => void
  ) => {
    if (!query.trim()) return;

    searchAbortControllersRef.current[type]?.abort();
    const controller = new AbortController();
    searchAbortControllersRef.current[type] = controller;
    setLoading(true);
    try {
      const url = resolve('meta_ads_targeting', {}, { type, query });
      const { data } = await axiosClient.get(url, {
        signal: controller.signal
      });
      const rows = Array.isArray(data?.data) ? data.data : [];

      if (type === 'cities') {
        setLocationOptions(rows.map(mapTargetingLocation));
      } else if (type === 'interests') {
        setInterestOptions(
          rows.map((row: any) => ({
            id: String(row.id),
            name: row.name,
            raw_data: sanitizeRawData(row.raw_data)
          }))
        );
      } else {
        setLanguageOptions(
          rows
            .map((row: any) => ({
              id: String(row.id || row.key || ''),
              name: row.name,
              raw_data: sanitizeRawData(row.raw_data)
            }))
            .filter((row: LomavisCampaignBuilderOption1) => row.id && row.name)
        );
      }
    } catch (error: any) {
      if (isCanceledRequest(error)) return;

      if (type === 'cities') {
        setLocationOptions([]);
      } else if (type === 'interests') {
        setInterestOptions([]);
      } else {
        setLanguageOptions([]);
      }
      lomavisShowToastr(
        t('adManager.targetingSearchFailed', 'Failed to search targeting.'),
        'error'
      );
    } finally {
      if (searchAbortControllersRef.current[type] === controller) {
        searchAbortControllersRef.current[type] = null;
        setLoading(false);
      }
    }
  };

  const handleTargetCountrySearch = async (
    query: string,
    setLoading: (loading: boolean) => void
  ) => {
    if (!query.trim()) {
      setTargetCountryOptions([]);
      return;
    }

    setLoading(true);
    try {
      const result: any = await dispatch(fetchMetaAdsCountries(query));
      const rows = Array.isArray(result?.data) ? result.data : [];
      setTargetCountryOptions(
        rows
          .map((row: any) => ({
            id: String(row.id || row.key || ''),
            name: row.name,
            raw_data: sanitizeRawData(row.raw_data)
          }))
          .filter((row: LomavisCampaignBuilderOption1) => row.id && row.name)
      );
    } catch (error) {
      setTargetCountryOptions([]);
      lomavisShowToastr(
        t('adManager.targetingSearchFailed', 'Failed to search targeting.'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const scrollToActionFooter = useCallback(() => {
    window.requestAnimationFrame(() => {
      actionFooterRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    });
  }, []);

  const saveCurrentDraft = async ({
    redirectToList = false,
    showSuccessToast,
    saveAsNewDraft = false,
    successMessage
  }: {
    redirectToList?: boolean;
    showSuccessToast: boolean;
    saveAsNewDraft?: boolean;
    successMessage?: string;
  }) => {
    if (!locationUuid || isMediaUploadActive) return null;

    try {
      return await dispatch(
        saveAdCampaignDraft({
          uuid: saveAsNewDraft ? undefined : form.uuid,
          payload: toDraftPayload(form, locationUuid),
          onSuccess: (draft) => {
            const nextForm = toFormState(
              draft,
              locationName,
              adManager.pixelOptions,
              adManager.pixelsLoaded,
              dsaDefaults,
              formCopyDefaults
            );
            setFormWithBaseline(nextForm);
            if (redirectToList) {
              dispatch(setActiveDraftUuid(null));
              navigateToAdManagerList();
            } else if (draft.uuid) {
              navigateToAdManagerDraft(draft.uuid);
            }
            if (showSuccessToast) {
              lomavisShowToastr(
                successMessage ||
                  t('adManager.draftSaved', 'Campaign draft saved.'),
                'success'
              );
            }
          },
          onError: (error) => {
            scrollToActionFooter();
            const fieldErrors = error?.response?.data || {};
            const summary = getFieldErrorSummary(fieldErrors, t);
            const message = summary[0]
              ? summary[0]
              : getRequestErrorMessage(
                  error,
                  t(
                    'adManager.draftSaveFailed',
                    'Failed to save campaign draft.'
                  ),
                  t
                );
            lomavisShowToastr(message, 'error');
          }
        })
      );
    } catch {
      return null;
    }
  };

  const notifyCampaignNameRequired = () => {
    lomavisShowToastr(
      t(
        'adManager.validation.campaignNameRequired',
        'Enter a campaign name.'
      ),
      'error'
    );
    setActiveTabKey('campaign');
  };

  const handleSaveDraft = async () => {
    if (isActiveDraftPublished) return;
    if (!hasCampaignName) {
      notifyCampaignNameRequired();
      return;
    }
    setPublishValidationErrors([]);
    await saveCurrentDraft({ redirectToList: true, showSuccessToast: true });
  };

  const handleSaveAsDraft = async () => {
    if (isActiveDraftPublished) return;
    if (!hasCampaignName) {
      notifyCampaignNameRequired();
      return;
    }
    setPublishValidationErrors([]);
    await saveCurrentDraft({
      redirectToList: true,
      showSuccessToast: true,
      saveAsNewDraft: true,
      successMessage: t(
        'adManager.draftSavedAsDraft',
        'Campaign saved as draft.'
      )
    });
  };

  const handlePublishCampaign = async () => {
    if (
      !locationUuid ||
      isMediaUploadActive ||
      isDraftActionActive ||
      shouldHideActiveDraftPublish
    ) {
      return;
    }

    setPublishAttempted(true);
    const validationErrors = validateAdManagerPublishForm(form);
    if (validationErrors.length > 0) {
      setPublishValidationErrors(validationErrors);
      setActiveTabKey(validationErrors[0].tabKey);
      scrollToActionFooter();
      lomavisShowToastr(
        t(
          'adManager.publishValidationFailed',
          'Complete the missing campaign details before publishing.'
        ),
        'error'
      );
      return;
    }

    const targetingOk = await runPrePublishTargetingCheck();
    if (!targetingOk) {
      setActiveTabKey('targeting');
      scrollToActionFooter();
      lomavisShowToastr(
        t(
          'adManager.targeting.deprecatedInterestsBlockPublish',
          'Some interests are no longer valid on Meta. Replace them before publishing.'
        ),
        'error'
      );
      return;
    }

    setPublishValidationErrors([]);
    setPublishBillingAcknowledged(false);
    setShowPublishConfirmModal(true);
  };

  const confirmPublishCampaign = async () => {
    if (!publishBillingAcknowledged) return;
    setShowPublishConfirmModal(false);

    if (
      !locationUuid ||
      isMediaUploadActive ||
      isDraftActionActive ||
      shouldHideActiveDraftPublish
    ) {
      return;
    }

    const savedDraft = await saveCurrentDraft({ showSuccessToast: false });
    if (!savedDraft?.uuid) return;

    try {
      await dispatch(
        publishAdCampaignDraft({
          uuid: savedDraft.uuid,
          onSuccess: (draft) => {
            const statusKey = getAdCampaignDraftStatusKey(draft);
            const publishStarted =
              statusKey === 'publishing' || statusKey === 'published';
            if (publishStarted) {
              lomavisShowToastr(
                t(
                  'adManager.campaignPublishQueued',
                  'Campaign publishing started. We will send you an email once Meta finishes reviewing it.'
                ),
                'success'
              );
              navigateToAdManagerList({ replace: true });
            } else {
              setFormFromDraft(draft);
              lomavisShowToastr(
                t(
                  'adManager.draftSavedAsDraft',
                  'Campaign saved as draft.'
                ),
                'success'
              );
            }
          },
          onError: (error) => {
            const failedCampaign = error?.response?.data?.campaign;
            if (failedCampaign?.uuid) {
              setFormFromDraft(failedCampaign);
            }
            const parsedTargetingError = parseDeprecatedInterestSuggestions(
              error?.response?.data ?? failedCampaign
            );
            if (parsedTargetingError?.suggestions.length) {
              setDeprecatedInterestSuggestions(parsedTargetingError.suggestions);
              setActiveTabKey('targeting');
              lomavisShowToastr(
                t(
                  'adManager.targeting.deprecatedInterestsBlockPublish',
                  'Some interests are no longer valid on Meta. Replace them before publishing.'
                ),
                'error'
              );
            } else {
              lomavisShowToastr(
                t(
                  'adManager.campaignPublishFailed',
                  'Failed to publish campaign.'
                ),
                'error'
              );
            }
            scrollToActionFooter();
          }
        })
      );
    } catch {}
  };

  const handleDeleteDraft = (uuid: string) => {
    if (isMediaUploadActive || isDraftActionActive) return;
    const draft = adManager.drafts.find(
      (row: AdCampaignDraft) => row.uuid === uuid
    );
    if (draft && !isAdCampaignDraftMutable(draft)) return;

    setPendingDeleteDraftUuid(uuid);
  };

  const confirmDeleteDraft = () => {
    const uuid = pendingDeleteDraftUuid;
    setPendingDeleteDraftUuid(null);
    if (!uuid) return;

    void dispatch(
      deleteAdCampaignDraft({
        uuid,
        onSuccess: () => {
          if (uuid === adUuid) {
            navigateToAdManagerList({ replace: true });
          }
          lomavisShowToastr(
            t('adManager.draftDeleted', 'Campaign draft deleted.'),
            'success'
          );
        },
        onError: () =>
          lomavisShowToastr(
            t(
              'adManager.draftDeleteFailed',
              'Failed to delete campaign draft.'
            ),
            'error'
          )
      })
    );
  };

  const handleCopyDraft = (uuid: string) => {
    if (isMediaUploadActive || isDraftActionActive || !uuid) return;

    void dispatch(
      copyAdCampaignDraft({
        uuid,
        copySuffix: t('adManager.copySuffix', 'copy'),
        onSuccess: (draft) => {
          setFormWithBaseline(
            toFormState(
              draft,
              locationName,
              adManager.pixelOptions,
              adManager.pixelsLoaded,
              dsaDefaults,
              formCopyDefaults
            )
          );
          setActivePreviewPlacementKey('facebook-feed');
          setActiveTabKey('campaign');
          if (draft.uuid) {
            navigateToAdManagerDraft(draft.uuid);
          }
          lomavisShowToastr(
            t('adManager.draftCopied', 'Campaign draft copied.'),
            'success'
          );
        },
        onError: () =>
          lomavisShowToastr(
            t('adManager.draftCopyFailed', 'Failed to copy campaign draft.'),
            'error'
          )
      })
    );
  };

  const handleToggleDisabled = (uuid: string, nextDisabled: boolean) => {
    if (!uuid) return;
    if (adManager.togglingDisabledDraftUuid) return;
    setPendingToggleDisabled({ uuid, disable: nextDisabled });
  };

  const confirmToggleDisabled = () => {
    const pending = pendingToggleDisabled;
    setPendingToggleDisabled(null);
    if (!pending) return;

    void dispatch(
      setAdCampaignDraftDisabled({
        uuid: pending.uuid,
        disabled: pending.disable,
        onSuccess: () => {
          lomavisShowToastr(
            pending.disable
              ? t('adManager.campaignDisabled', 'Campaign disabled.')
              : t('adManager.campaignEnabled', 'Campaign enabled.'),
            'success'
          );
        },
        onError: (error) => {
          const detail =
            typeof error?.response?.data?.detail === 'string'
              ? error.response.data.detail
              : '';
          const fallback = pending.disable
            ? t('adManager.campaignDisableFailed', 'Failed to disable campaign.')
            : t('adManager.campaignEnableFailed', 'Failed to enable campaign.');
          lomavisShowToastr(detail || fallback, 'error');
        }
      })
    ).catch(() => undefined);
  };

  const campaignTableColumns = getCampaignReviewTableColumns({
    draftActionsDisabled: isMediaUploadActive || isDraftActionActive,
    copyingDraftId: adManager.copyingDraftUuid,
    deletingDraftId: adManager.deletingDraftUuid,
    togglingDisabledDraftId: adManager.togglingDisabledDraftUuid,
    onDraftCopy: handleCopyDraft,
    onDraftDelete: handleDeleteDraft,
    onDraftEdit: openDraftEditor,
    onDraftToggleDisabled: handleToggleDisabled,
    t
  });

  const hasSelectedPlacements = form.placements.some(
    (placement) => placement.selected
  );

  const handleMediaFiles = async (mediaFiles: File[]) => {
    if (!locationUuid || isMediaUploadActive) return;

    setMediaValidationNotice(null);

    if (!hasSelectedPlacements) return;

    if (
      form.selectedMediaFiles.length + mediaFiles.length >
      AD_MANAGER_MEDIA_UPLOAD_MAX_FILES
    ) {
      lomavisShowToastr(
        t(
          'adManager.mediaUploadLimitReached',
          'You can upload up to {maxFiles} media files. Remove a file before adding another.',
          { maxFiles: AD_MANAGER_MEDIA_UPLOAD_MAX_FILES }
        ),
        'error'
      );
      return;
    }

    const alreadySelectedVideoCount = form.selectedMediaFiles.filter(
      (file) => file.kind === 'video'
    ).length;
    const incomingVideoCount = mediaFiles.filter(
      (file) => getMediaKind(file) === 'video'
    ).length;
    if (
      alreadySelectedVideoCount + incomingVideoCount >
      AD_MANAGER_MEDIA_UPLOAD_MAX_VIDEOS
    ) {
      lomavisShowToastr(
        t(
          'adManager.videoUploadLimitReached',
          'You can only upload one video per campaign. Remove the current video before adding another.'
        ),
        'error'
      );
      return;
    }

    const unsupportedMediaFiles = getUnsupportedMediaFiles(mediaFiles);
    if (unsupportedMediaFiles.length > 0) {
      const unsupportedFileNames = formatFileNameList(unsupportedMediaFiles, t);
      lomavisShowToastr(
        `${t(
          'adManager.unsupportedMediaFormat',
          'Unsupported file format'
        )}: ${unsupportedFileNames}. ${t(
          'adManager.supportedMediaFormatHelp',
          'Upload {formats} files only.',
          { formats: SUPPORTED_MEDIA_FORMATS_LABEL }
        )}`,
        'error'
      );
      return;
    }

    if (hasMixedMediaUpload(mediaFiles, form.selectedMediaFiles)) {
      lomavisShowToastr(
        t(
          'adManager.mixedMediaUploadNotAllowed',
          'Use either images (JPEG/PNG) or videos (MP4/MOV) in one campaign, not both.'
        ),
        'error'
      );
      return;
    }

    const filesToUpload = getUploadableMediaFiles(
      mediaFiles,
      form.selectedMediaFiles
    );
    if (filesToUpload.length === 0) return;

    const selectedPlacementIds = form.placements
      .filter((placement) => placement.selected)
      .map((placement) => placement.id);
    const placementCategories = getPlacementCategoriesFromKeys(
      selectedPlacementIds
    );
    const validationResults = await Promise.all(
      filesToUpload.map(async (file) => {
        const kind = getMediaKind(file);
        if (kind !== 'image' && kind !== 'video') {
          return { file, result: { errors: [], warnings: [] } };
        }
        const metadata = await getMediaMetadata(file, file.type);
        return {
          file,
          result: validateMediaConstraints(
            {
              size: file.size,
              width: (metadata as { width?: number }).width,
              height: (metadata as { height?: number }).height,
              kind
            },
            placementCategories
          )
        };
      })
    );

    const validationNotice = buildMediaValidationNotice(
      validationResults.map((entry) => ({
        fileName: entry.file.name,
        result: entry.result
      })),
      placementCategories.length,
      (descriptor) => t(descriptor.key, descriptor.fallback, descriptor.detail)
    );
    if (validationNotice) {
      setMediaValidationNotice(validationNotice);
      if (validationNotice.level === 'error') return;
    }

    const uploadDraftUuid = form.uuid || null;

    try {
      for (const rawFile of filesToUpload) {
        const uploadController = new AbortController();
        uploadAbortControllerRef.current = uploadController;
        cancelUploadRequestedRef.current = false;
        setUploadProgress({ fileName: rawFile.name, percent: 0 });
        const file = isCompressibleImage(rawFile)
          ? await compressImage(rawFile)
          : rawFile;
        const uploadPreset = await dispatch(
          uploadAdManagerMediaFile({
            contentType: getSupportedMediaContentType(file) || file.type,
            file,
            locationUuid,
            onProgress: (percent) =>
              setUploadProgress({ fileName: file.name, percent }),
            signal: uploadController.signal
          })
        );
        uploadAbortControllerRef.current = null;
        if (formDraftUuidRef.current !== uploadDraftUuid) break;

        const uploadedMediaUuid = getUploadPresetMediaUuid(uploadPreset);
        if (!uploadedMediaUuid) {
          throw new Error('Missing uploaded media UUID.');
        }

        setUploadProgress({ fileName: file.name, percent: 100 });

        const previewUrl = URL.createObjectURL(file);
        objectUrlsRef.current.add(previewUrl);

        const uploadedFile: SelectedMediaFile = {
          id: uploadedMediaUuid,
          imageUuid: uploadedMediaUuid,
          kind: getMediaKind(file),
          meta: formatFileSize(file.size),
          name: file.name,
          previewUrl
        };

        setForm((currentForm) => ({
          ...currentForm,
          selectedMediaFiles: [...currentForm.selectedMediaFiles, uploadedFile]
        }));
        setPublishValidationErrors([]);
      }
    } catch (error: any) {
      if (isCanceledRequest(error) || cancelUploadRequestedRef.current) {
        return;
      }
      lomavisShowToastr(
        t('adManager.mediaUploadFailed', 'Failed to upload media.'),
        'error'
      );
      throw error;
    } finally {
      uploadAbortControllerRef.current = null;
      cancelUploadRequestedRef.current = false;
      setUploadProgress(null);
    }
  };

  const handleCancelMediaUpload = () => {
    cancelUploadRequestedRef.current = true;
    uploadAbortControllerRef.current?.abort();
    setUploadProgress(null);
  };

  const handleMediaFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = '';
    void handleMediaFiles(files).catch(() => undefined);
  };

  const handleMediaFilesRef = useRef(handleMediaFiles);
  handleMediaFilesRef.current = handleMediaFiles;

  useEffect(() => {
    if (!isEditorRoute) return;

    const isFileDrag = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const onDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    };

    const onDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      void handleMediaFilesRef.current(files).catch(() => undefined);
    };

    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [isEditorRoute]);

  const openMediaLibrary = useCallback(() => {
    if (!locationslug || isMediaUploadActive) return;

    const remainingSlots =
      AD_MANAGER_MEDIA_UPLOAD_MAX_FILES - form.selectedMediaFiles.length;
    if (remainingSlots <= 0) {
      showMediaUploadLimitReached();
      return;
    }

    const selectedMediaKind = getSelectedMediaKind(form.selectedMediaFiles);
    const mediaLibraryPath = generatePath('/locations/:locationslug/media/', {
      locationslug
    });
    const queryParams = new URLSearchParams({
      mode: 'popup',
      trigger: AD_MANAGER_MEDIA_LIBRARY_TRIGGER,
      openCropper: 'false',
      maxMediaFiles: String(remainingSlots),
      videoSelectAllowed: String(selectedMediaKind !== 'image')
    });
    const width = 1000;
    const height = 600;
    const left = Math.max(0, (window.innerWidth - width) / 2);
    const top = 100;
    const popupParams = [
      'scrollbars=no',
      'resizable=no',
      'status=no',
      'location=no',
      'toolbar=no',
      'menubar=no',
      'directories=no',
      'copyhistory=no',
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`
    ].join(',');
    const mediaLibraryWindow = window.open(
      `${window.location.origin}${mediaLibraryPath}?${queryParams.toString()}`,
      'mediaLibraryPopup',
      popupParams
    );

    mediaLibraryWindow?.focus();
  }, [
    form.selectedMediaFiles,
    isMediaUploadActive,
    locationslug,
    showMediaUploadLimitReached
  ]);

  const handleFileRemove = (id: string) => {
    const fileToRemove = form.selectedMediaFiles.find((file) => file.id === id);
    if (
      fileToRemove?.previewUrl &&
      objectUrlsRef.current.has(fileToRemove.previewUrl)
    ) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
      objectUrlsRef.current.delete(fileToRemove.previewUrl);
    }

    updateForm({
      selectedMediaFiles: form.selectedMediaFiles.filter(
        (file) => file.id !== id
      )
    });
  };

  const fieldErrorSummary = getFieldErrorSummary(adManager.fieldErrors, t);
  const publishValidationSummary = getPublishValidationSummary(
    publishValidationErrors,
    t
  );
  const shouldShowEditorActionIssues =
    publishAttempted ||
    fieldErrorSummary.length > 0 ||
    (isEditorRoute && Boolean(adManager.error));
  const editorActionErrorSummary = shouldShowEditorActionIssues
    ? Array.from(
        new Set([
          ...publishValidationSummary,
          ...fieldErrorSummary,
          ...(isEditorRoute && adManager.error
            ? [getTranslatedAdManagerErrorMessage(adManager.error, t)]
            : [])
        ])
      )
    : [];
  const editorActionIssues = editorActionErrorSummary.map((message) => ({
    id: message,
    message
  }));
  const topErrorSummary = isEditorRoute ? [] : fieldErrorSummary;
  const topErrorMessage = isEditorRoute ? null : adManager.error;

  if (locationLoadError) {
    return (
      <Container
        style={{ height: `calc(100vh - ${navBarHeight}px)` }}
        className="w-100"
      >
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <Alert variant="danger" className="w-100">
            <div className="fw-bold">
              {t('adManager.locationLoadErrorTitle', "Couldn't load location")}
            </div>
            <div className="mt-1">{locationLoadError}</div>
            <Button
              type="button"
              variant="outline-danger"
              className="mt-3"
              onClick={() => {
                void loadLocationDetails();
              }}
            >
              {t('common.retry', 'Retry')}
            </Button>
          </Alert>
        </div>
      </Container>
    );
  }

  if (subscriptionError) {
    return (
      <Container
        style={{ height: `calc(100vh - ${navBarHeight}px)` }}
        className="w-100"
      >
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <Alert variant="danger" className="w-100">
            <div className="fw-bold">
              {t(
                'adManager.subscriptionLoadErrorTitle',
                "Couldn't load subscription"
              )}
            </div>
            <div className="mt-1">{subscriptionError}</div>
            <Button
              type="button"
              variant="outline-danger"
              className="mt-3"
              onClick={() => {
                void loadSubscriptionData();
              }}
            >
              {t('common.retry', 'Retry')}
            </Button>
          </Alert>
        </div>
      </Container>
    );
  }

  const isLocationStillLoading = locationLoading || !locationUuid;

  if (
    !isLocationStillLoading &&
    (!metaAdAccounts || metaAdAccounts.length === 0)
  ) {
    return (
      <Container
        style={{ height: `calc(100vh - ${navBarHeight}px)` }}
        className="w-100"
      >
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
            <span className="fs-3">{t('adsAnalytics.noAdAccount')}</span>
            <img
              src={PLATFORM_STATIC_ICONS_PNG[META]}
              className="h-25px"
              style={{ marginRight: 2 }}
            />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container
      style={{ height: `calc(100vh - ${navBarHeight}px)` }}
      className="w-100"
    >
      <LomavisFeatureAvailable1
        isAvailable={paidAdSub || isLoadingSubscription}
        title={t('adsAnalytics.notAvailable')}
      >
        <div className="bg-light px-4 pt-2">
          <AnalyticsNavbar
            actions={adManagerNavbarActions}
            className="rounded-3 lomavis-ad-manager-navbar"
          />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={AD_MANAGER_MEDIA_ACCEPT}
          multiple
          hidden
          onChange={(event) => {
            void handleMediaFileChange(event);
          }}
        />
        {(topErrorMessage || topErrorSummary.length > 0) && (
          <Alert variant="danger" className="mx-4 mt-3 mb-0">
            {topErrorMessage && <div>{topErrorMessage}</div>}
            {topErrorSummary.map((error) => (
              <div key={error}>{error}</div>
            ))}
          </Alert>
        )}
        {!isEditorRoute ? (
          <div className="bg-light px-4 py-4">
            <LomavisDataTable1
              actions={
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Select
                    size="sm"
                    aria-label={t(
                      'adManager.table.statusFilterAriaLabel',
                      'Filter campaigns by status'
                    )}
                    value={draftStatusFilter}
                    onChange={(event) =>
                      setDraftStatusFilter(
                        event.currentTarget.value as typeof draftStatusFilter
                      )
                    }
                    style={{ width: 'auto' }}
                  >
                    <option value="all">
                      {t('adManager.table.statusFilterAll', 'All statuses')}
                    </option>
                    <option value="draft">
                      {t('adManager.status.draft', 'Draft')}
                    </option>
                    <option value="publishing">
                      {t('adManager.status.publishing', 'Publishing')}
                    </option>
                    <option value="published">
                      {t('adManager.status.published', 'Published')}
                    </option>
                    <option value="failed">
                      {t('adManager.status.failed', 'Failed')}
                    </option>
                  </Form.Select>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={isMediaUploadActive || isDraftActionActive}
                    onClick={openNewDraftEditor}
                  >
                    {t('adManager.createCampaign', 'Create Campaign')}
                  </Button>
                </div>
              }
              ariaLabel={t('adManager.table.ariaLabel', 'Campaigns table')}
              columns={campaignTableColumns}
              loading={isDraftTableLoading}
              loadingLabel={t(
                'adManager.loadingCampaigns',
                'Loading campaigns...'
              )}
              minWidth={1120}
              onRowClick={(row) => {
                const draftId = String(row.id ?? '');
                openDraftEditor(draftId);
              }}
              rows={reviewRows}
              subtitle={
                adManager.loadingDrafts
                  ? t('adManager.loadingCampaigns', 'Loading campaigns...')
                  : t(
                      'adManager.campaignsSubtitle',
                      'Manage your ad campaigns.'
                    )
              }
              title={t('adManager.campaigns', 'Campaigns')}
            />
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={
                  adManager.loadingDrafts || !adManager.draftsPreviousUrl
                }
                onClick={() => {
                  void loadDraftsPage(adManager.draftsPreviousUrl);
                }}
              >
                <i
                  className="fa-solid fa-chevron-left me-2"
                  aria-hidden="true"
                />
                {t('pagination.previous', 'Previous')}
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={adManager.loadingDrafts || !adManager.draftsNextUrl}
                onClick={() => {
                  void loadDraftsPage(adManager.draftsNextUrl);
                }}
              >
                {t('pagination.next', 'Next')}
                <i
                  className="fa-solid fa-chevron-right ms-2"
                  aria-hidden="true"
                />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-light">
              <div className="container-xxl px-4">
                <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                  <div className="d-inline-flex align-items-center gap-3 flex-wrap">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      disabled={isMediaUploadActive || isDraftActionActive}
                      onClick={closeEditor}
                    >
                      {t('adManager.backToCampaigns', 'Back to campaigns')}
                    </Button>
                    {form.uuid && activeDraft
                      ? (() => {
                          const topStatusKey: LomavisCampaignReviewRowStatusKey1 =
                            isActiveDraftPublishing
                              ? 'publishing'
                              : activeDraftStatusKey;
                          // Mirror the badge classes used by the campaigns
                          // table so the two surfaces read the same at a
                          // glance. Only shown once a draft actually exists —
                          // during initial creation there's no status to show.
                          const badgeClassByStatus: Record<
                            LomavisCampaignReviewRowStatusKey1,
                            string
                          > = {
                            draft: 'text-bg-warning',
                            publishing: 'text-bg-info',
                            published: 'text-bg-success',
                            failed: 'text-bg-danger'
                          };
                          const label = getAdCampaignDraftStatusLabel(
                            activeDraft,
                            isActiveDraftPublishing,
                            t
                          );
                          return (
                            <span
                              className={`badge rounded-pill text-uppercase fw-bold ${badgeClassByStatus[topStatusKey]}`}
                            >
                              <span className="me-1" aria-hidden="true">
                                ·
                              </span>
                              {label}
                            </span>
                          );
                        })()
                      : null}
                  </div>
                  {form.uuid && isActiveDraftPublished ? (
                    <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
                      <input
                        id="ad-manager-detail-enabled-toggle"
                        className="form-check-input m-0"
                        type="checkbox"
                        role="switch"
                        checked={!(activeDraft?.is_disabled === true)}
                        disabled={
                          !activeDraft ||
                          isMediaUploadActive ||
                          isDraftActionActive ||
                          adManager.togglingDisabledDraftUuid === form.uuid
                        }
                        onChange={() => {
                          if (!form.uuid) return;
                          handleToggleDisabled(
                            form.uuid,
                            !(activeDraft?.is_disabled === true)
                          );
                        }}
                      />
                      <label
                        htmlFor="ad-manager-detail-enabled-toggle"
                        className="form-check-label small fw-semibold m-0 d-inline-flex align-items-center gap-2"
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-block',
                            height: 10,
                            width: 10,
                            borderRadius: '50%',
                            backgroundColor:
                              activeDraft?.is_disabled === true
                                ? '#9aa0a6'
                                : '#22c55e'
                          }}
                        />
                        {activeDraft?.is_disabled === true
                          ? t('adManager.status.disabled', 'Disabled')
                          : t('adManager.status.enabled', 'Enabled')}
                      </label>
                    </div>
                  ) : null}
                </div>
                <AdManagerStatusBanner
                  statusKey={
                    isActiveDraftPublishing
                      ? 'publishing'
                      : activeDraftStatusKey
                  }
                  publishedAt={activeDraft?.published_at}
                  failureMessage={activeDraft?.failure_message}
                  metaCampaignId={activeDraft?.meta_campaign_id}
                />
              </div>
            </div>
            <LomavisCampaignBuilder1
              campaignName={form.campaignName}
              onCampaignNameChange={(campaignName) =>
                updateForm({ campaignName })
              }
              audienceEstimate={
                isAudienceEstimateLoading ? (
                  '...'
                ) : (
                  <AdManagerAudienceEstimate estimate={audienceEstimate} />
                )
              }
              activeTabKey={activeTabKey}
              onTabChange={setActiveTabKey}
              placements={form.placements}
              onPlacementToggle={(id) =>
                updateForm({
                  placements: form.placements.map((placement) =>
                    placement.id === id
                      ? { ...placement, selected: !placement.selected }
                      : placement
                  )
                })
              }
              objective={form.objective}
              onObjectiveChange={(objective) =>
                updateForm({
                  objective,
                  ...(isPixelTrackingObjective(objective)
                    ? {}
                    : { pixel: null }),
                  ...(isConversionTrackingObjective(objective)
                    ? {}
                    : { targetEvent: null })
                })
              }
              locationOptions={locationOptions}
              locations={form.locations}
              onLocationsChange={(locations) =>
                updateForm(normalizeSpecialAdFormUpdates({ locations }))
              }
              onLocationSearch={(query, setLoading) => {
                void handleSearch('cities', query, setLoading);
              }}
              targetCountryOptions={targetCountryOptions}
              targetCountries={form.targetCountries}
              onTargetCountriesChange={(targetCountries) =>
                updateForm(normalizeSpecialAdFormUpdates({ targetCountries }))
              }
              onTargetCountrySearch={(query, setLoading) => {
                void handleTargetCountrySearch(query, setLoading);
              }}
              radiusKm={form.radiusKm}
              radiusMinKm={
                hasRestrictedSpecialAdCategory(form.specialAdCategories)
                  ? SPECIAL_AD_CATEGORY_MIN_RADIUS_KM
                  : MIN_RADIUS_KM
              }
              radiusMaxKm={MAX_RADIUS_KM}
              onRadiusChange={(radiusKm) =>
                updateForm(
                  normalizeSpecialAdFormUpdates({
                    radiusKm: clampRadiusKm(radiusKm)
                  })
                )
              }
              ageRange={form.ageRange}
              onAgeRangeChange={(ageRange) =>
                updateForm(
                  normalizeSpecialAdFormUpdates({
                    ageRange: [
                      clampAge(ageRange[0]),
                      clampAge(ageRange[1])
                    ] as [number, number]
                  })
                )
              }
              gender={form.gender}
              onGenderChange={(gender) =>
                updateForm(normalizeSpecialAdFormUpdates({ gender }))
              }
              languageOptions={languageOptions}
              languages={form.languages}
              onLanguagesChange={(languages) => updateForm({ languages })}
              onLanguageSearch={(query, setLoading) => {
                void handleSearch('languages', query, setLoading);
              }}
              interestOptions={interestOptions}
              interests={form.interests}
              onInterestsChange={(interests) =>
                updateForm(normalizeSpecialAdFormUpdates({ interests }))
              }
              onInterestSearch={(query, setLoading) => {
                void handleSearch('interests', query, setLoading);
              }}
              behaviors={form.behaviors}
              customAudiences={form.customAudiences}
              onCustomAudiencesChange={(customAudiences) =>
                updateForm({ customAudiences })
              }
              customAudienceOptions={customAudienceOptions}
              isLoadingCustomAudiences={isLoadingCustomAudiences}
              customAudiencesError={customAudiencesError}
              onRetryCustomAudiences={() => {
                void loadCustomAudiences();
              }}
              interestsSectionSlot={
                activeDeprecatedInterestSuggestions.length > 0 ? (
                  <DeprecatedInterestsNotice
                    suggestions={activeDeprecatedInterestSuggestions}
                    onReplace={replaceDeprecatedInterest}
                    onRemove={removeDeprecatedInterest}
                    onReplaceAll={replaceAllDeprecatedInterests}
                  />
                ) : null
              }
              onBehaviorRemove={(id) =>
                updateForm(
                  normalizeSpecialAdFormUpdates({
                    behaviors: form.behaviors.filter(
                      (behavior) => String(behavior.id) !== id
                    )
                  })
                )
              }
              specialAdCategories={form.specialAdCategories}
              onSpecialAdCategoriesChange={(specialAdCategories) =>
                updateForm(
                  normalizeSpecialAdFormUpdates({ specialAdCategories })
                )
              }
              countryOptions={adManager.countryOptions}
              specialAdCategoryCountry={form.specialAdCategoryCountry}
              onSpecialAdCategoryCountryChange={(specialAdCategoryCountry) =>
                updateForm({ specialAdCategoryCountry })
              }
              pixel={form.pixel}
              pixelOptions={adManager.pixelOptions}
              isLoadingPixels={adManager.loadingPixels}
              onPixelChange={(pixel) =>
                updateForm({
                  pixel: pixel
                    ? { id: String(pixel.id), name: pixel.name }
                    : null
                })
              }
              targetEvent={form.targetEvent}
              onTargetEventChange={(targetEvent) =>
                updateForm({
                  targetEvent: targetEvent
                    ? { id: String(targetEvent.id), name: targetEvent.name }
                    : null
                })
              }
              ad={{
                ...form.ad,
                accountName: activePreviewProfile.name,
                profileImageUrl: activePreviewProfile.imageUrl,
                sponsoredLabel: t('adManager.preview.sponsored', 'Sponsored')
              }}
              onAdChange={(ad) =>
                updateForm({
                  ad: {
                    accountName: form.ad.accountName,
                    sponsoredLabel: form.ad.sponsoredLabel,
                    primaryText: ad.primaryText,
                    landingUrl: ad.landingUrl,
                    cta: ad.cta
                  }
                })
              }
              selectedMediaFiles={form.selectedMediaFiles}
              onFileRemove={handleFileRemove}
              activePreviewPlacementKey={activePreviewPlacementKey}
              onPreviewPlacementChange={setActivePreviewPlacementKey}
              onBrowseMedia={() => fileInputRef.current?.click()}
              onOpenMediaLibrary={openMediaLibrary}
              onMediaFilesDrop={(files) => {
                void handleMediaFiles(files).catch(() => undefined);
              }}
              onCancelUpload={handleCancelMediaUpload}
              uploadProgress={uploadProgress}
              mediaUploadDisabled={!hasSelectedPlacements}
              mediaValidationNotice={
                mediaValidationNotice
                  ? {
                      level: mediaValidationNotice.level,
                      title:
                        mediaValidationNotice.level === 'error'
                          ? t(
                              'adManager.mediaValidation.errorTitle',
                              'Some files can’t be uploaded'
                            )
                          : t(
                              'adManager.mediaValidation.warningTitle',
                              'Heads up before you publish'
                            ),
                      items: mediaValidationNotice.items
                    }
                  : !hasSelectedPlacements
                  ? {
                      level: 'info',
                      title: t(
                        'adManager.mediaValidation.noPlacementsTitle',
                        'Select placements before uploading media'
                      ),
                      items: [
                        {
                          message: t(
                            'adManager.mediaValidation.noPlacementsBody',
                            'Which aspect ratios and formats are allowed depends on your placement selection. Pick at least one Facebook or Instagram placement first, then upload.'
                          )
                        }
                      ]
                    }
                  : null
              }
              budgetType={form.budgetType}
              onBudgetTypeChange={(budgetType) => updateForm({ budgetType })}
              amount={form.amount}
              onAmountChange={(amount) => updateForm({ amount })}
              startDate={form.startDate}
              onStartDateChange={(startDate) => updateForm({ startDate })}
              endDate={form.endDate}
              onEndDateChange={(endDate) => updateForm({ endDate })}
              bidStrategy={form.bidStrategy}
              onBidStrategyChange={(bidStrategy) =>
                updateForm({
                  bidStrategy,
                  ...(bidStrategy === 'automatic' ? { maxBidAmount: '' } : {})
                })
              }
              maxBidAmount={form.maxBidAmount}
              onMaxBidAmountChange={(maxBidAmount) =>
                updateForm({ maxBidAmount })
              }
              currency={activeBudgetCurrency}
              dsaBeneficiary={form.dsaBeneficiary}
              onDsaBeneficiaryChange={(dsaBeneficiary) =>
                updateForm({ dsaBeneficiary })
              }
              dsaPayor={form.dsaPayor}
              onDsaPayorChange={(dsaPayor) => updateForm({ dsaPayor })}
              estimatedTotalLabel={
                form.amount
                  ? formatCurrencyAmountLabel(form.amount, activeBudgetCurrency)
                  : '-'
              }
              estimatedPeriodLabel={
                form.budgetType === 'daily'
                  ? t('adManager.budget.perDay', '/ day')
                  : t('adManager.budget.total', 'total')
              }
              isSaving={adManager.savingDraft}
              isPublishing={
                Boolean(adManager.publishingDraftUuid) ||
                isActiveDraftPublishing ||
                isTargetingCheckLoading
              }
              isUploadingMedia={isMediaUploadActive}
              saveActionLabel={t('adManager.save', 'Save')}
              saveActionDisabled={
                !locationUuid ||
                !hasCampaignName ||
                isMediaUploadActive ||
                isDraftActionActive
              }
              onSave={
                isActiveDraftPublished || isCreateRoute
                  ? undefined
                  : () => {
                      void handleSaveDraft();
                    }
              }
              saveAsDraftActionLabel={t(
                'adManager.saveAsDraft',
                'Save as draft'
              )}
              onSaveAsDraft={
                isActiveDraftPublished
                  ? undefined
                  : isCreateRoute
                  ? () => {
                      void handleSaveDraft();
                    }
                  : () => {
                      void handleSaveAsDraft();
                    }
              }
              cancelActionLabel={t('common.cancel', 'Cancel')}
              cancelActionDisabled={isMediaUploadActive || isDraftActionActive}
              onCancel={closeEditor}
              cloneActionLabel={t('adManager.cloneCampaign', 'Clone Campaign')}
              cloneActionDisabled={
                !form.uuid || isMediaUploadActive || isDraftActionActive
              }
              onClone={
                form.uuid ? () => handleCopyDraft(form.uuid!) : undefined
              }
              showOnlyCloneAction={isActiveDraftPublished}
              primaryActionLabel={t('adManager.publish', 'Publish')}
              primaryActionDisabled={
                !locationUuid || isMediaUploadActive || isDraftActionActive
              }
              hidePrimaryAction={shouldHideActiveDraftPublish}
              actionValidationIssues={editorActionIssues}
              actionFooterRef={actionFooterRef}
              onPublish={() => {
                void handlePublishCampaign();
              }}
              tabsRightSlot={
                form.uuid && locationslug ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        generatePath(PATH_PAGE.ad_manager_draft_activity, {
                          adUuid: form.uuid!,
                          locationslug
                        })
                      )
                    }
                    className="bg-transparent border-0 px-0 text-muted fw-semibold"
                    style={{ cursor: 'pointer', outline: 'none' }}
                  >
                    <div className="d-flex flex-column align-items-center pt-3 px-3 gap-1 fs-6">
                      <span className="d-inline-flex align-items-center gap-2">
                        <i
                          className="fa-regular fa-clock-rotate-left"
                          aria-hidden="true"
                        />
                        <span>
                          {t('adManager.tabs.activity', 'Activity')}
                        </span>
                      </span>
                      <span
                        aria-hidden
                        style={{ height: 4, marginBottom: -2, opacity: 0 }}
                      />
                    </div>
                  </button>
                ) : null
              }
            />
          </>
        )}
      </LomavisFeatureAvailable1>
      <ConfirmModal
        show={pendingDeleteDraftUuid !== null}
        handleClose={() => setPendingDeleteDraftUuid(null)}
        handleConfirm={confirmDeleteDraft}
        title={t('adManager.deleteConfirmTitle', 'Delete campaign draft?')}
        message={t(
          'adManager.deleteConfirm',
          'Delete this campaign draft?'
        )}
        showNextButton={false}
      />
      <ConfirmModal
        show={showPublishConfirmModal}
        handleClose={() => setShowPublishConfirmModal(false)}
        handleConfirm={() => {
          void confirmPublishCampaign();
        }}
        title={t(
          'adManager.publishConfirmTitle',
          'Publish this campaign?'
        )}
        message={
          <Form.Check
            type="checkbox"
            id="ad-manager-publish-billing-ack"
            data-testid="ad-manager-publish-billing-ack"
            checked={publishBillingAcknowledged}
            onChange={(e) =>
              setPublishBillingAcknowledged(e.target.checked)
            }
            label={
              <span style={{ color: '#000' }}>
                {t(
                  'adManager.publishBillingAcknowledgement',
                  'I understand that Meta will bill the connected ad account for this campaign and that publishing may cause money to be spent.'
                )}
              </span>
            }
          />
        }
        showNextButton={false}
        confirmDisabled={!publishBillingAcknowledged}
      />
      <ConfirmModal
        show={pendingToggleDisabled !== null}
        handleClose={() => setPendingToggleDisabled(null)}
        handleConfirm={confirmToggleDisabled}
        title={
          pendingToggleDisabled?.disable
            ? t('adManager.disableConfirmTitle', 'Disable this campaign?')
            : t('adManager.enableConfirmTitle', 'Enable this campaign?')
        }
        message={
          pendingToggleDisabled?.disable
            ? t(
                'adManager.disableConfirmMessage',
                'Disabling pauses this campaign on Meta. You can re-enable it anytime.'
              )
            : t(
                'adManager.enableConfirmMessage',
                'Re-enable this campaign on Meta?'
              )
        }
        showNextButton={false}
      />
    </Container>
  );
};

export default AdManagerPage;
